package features.booking

import network.FirebaseClient
import kotlinx.coroutines.flow.first
import kotlin.time.Clock
import io.ktor.client.call.body
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.contentType

interface UserRepository {
    suspend fun signIn(email: String, password: String): String
    suspend fun getUserRole(uid: String): String
    suspend fun getUsuariosApp(): List<UsuarioApp>
    suspend fun actualizarRolUsuario(uid: String, nuevoRol: String): Boolean
    suspend fun programarCambioRol(uidUsuario: String, nuevoRol: String, tiempoEfectivo: Long): Boolean
    suspend fun ejecutarRolPendiente(uid: String, nuevoRol: String): Boolean
    suspend fun solicitarCambioRol(uidUsuario: String, nuevoRol: String, uidSolicitante: String): Boolean
    suspend fun aprobarCambioRol(uidUsuario: String, rolAprobado: String): Boolean
    suspend fun declinarCambioRol(uidUsuario: String): Boolean
    suspend fun solicitarDegradoChofer(uidChofer: String, uidSolicitante: String): Boolean
    suspend fun aprobarDegrado(uidChofer: String): Boolean
    suspend fun declinarDegrado(uidChofer: String): Boolean
    suspend fun getUserName(uid: String): String?

    suspend fun crearCuenta(email: String, password: String, nombre: String, rol: String): Boolean
    suspend fun eliminarCuenta(uid: String): Boolean
    suspend fun resetearContrasena(uid: String, adminUid: String): String?
    suspend fun requiereCambioContrasena(uid: String): Boolean
    suspend fun cambiarContrasena(nuevaContrasena: String): Boolean
    suspend fun limpiarFlagCambio(uid: String): Boolean

    suspend fun limpiarCambioPendiente(uid: String): Boolean
}

class UserRepositoryImpl : UserRepository {
    override suspend fun signIn(email: String, password: String): String {
        return try {
            val auth = FirebaseClient.auth
            val result = auth.signInWithEmailAndPassword(email, password)
            result.user?.uid ?: throw Exception("No se pudo obtener el ID de usuario")
        } catch (e: Exception) { throw e }
    }

    override suspend fun getUserRole(uid: String): String {
        return try {
            val snapshot = network.FirebaseClient.database.reference("usuarios/$uid").valueEvents.first()
            if (!snapshot.exists) return ""
            // Leer rol directamente (intentar sin y con dos puntos por legacy)
            val rolRaw = try { snapshot.child("rol").value<String>() } catch (e: Exception) { null }
                ?: try { snapshot.child("rol:").value<String>() } catch (e: Exception) { "" }
            var rolReal = rolRaw.lowercase().trim()
            val rolProgramado = try { snapshot.child("rolProgramado").value<String>() } catch (e: Exception) { null }
            val fechaCambio = try { snapshot.child("fechaCambioProgramado").value<String>()?.toLongOrNull() } catch (e: Exception) { null }
            if (rolProgramado != null && fechaCambio != null) {
                val tiempoActual = Clock.System.now().toEpochMilliseconds()
                if (tiempoActual >= fechaCambio) {
                    ejecutarRolPendiente(uid, rolProgramado)
                    rolReal = rolProgramado.lowercase().trim()
                }
            }
            // Restaura el modo prueba de pagos guardado: debe sobrevivir a recargar la página.
            network.ModoPruebaPagos.cargarDeUsuario(uid)
            rolReal
        } catch (e: Exception) {
            ""
        }
    }
    override suspend fun getUserName(uid: String): String? {
        return try {
            // Try "nombre" first, fallback to "nombre:" for legacy data
            val snapshot = FirebaseClient.database
                .reference("usuarios/$uid/nombre")
                .valueEvents.first()
            if (snapshot.exists) {
                snapshot.value<String>()
            } else {
                val snapshotLegacy = FirebaseClient.database
                    .reference("usuarios/$uid/nombre:")
                    .valueEvents.first()
                if (snapshotLegacy.exists) snapshotLegacy.value<String>() else null
            }
        } catch (e: Exception) {
            null
        }
    }

    override suspend fun getUsuariosApp(): List<UsuarioApp> {
        return try {
            val db = network.FirebaseClient.database
            val snapshot = db.reference("usuarios").valueEvents.first()
            if (!snapshot.exists) return emptyList()
            val listaUsuarios = mutableListOf<UsuarioApp>()
            // Iterar children en vez de deserializar todo (evita crash si hay campos no-string)
            snapshot.children.forEach { userSnap ->
                try {
                    val uid = userSnap.key ?: return@forEach
                    val nombre = try { userSnap.child("nombre").value<String>() } catch (e: Exception) { null }
                        ?: try { userSnap.child("nombre:").value<String>() } catch (e: Exception) { "Sin Nombre" }
                    val correo = try { userSnap.child("correo").value<String>() } catch (e: Exception) { null }
                        ?: try { userSnap.child("correo:").value<String>() } catch (e: Exception) { "Sin Correo" }
                    val rol = try { userSnap.child("rol").value<String>() } catch (e: Exception) { null }
                        ?: try { userSnap.child("rol:").value<String>() } catch (e: Exception) { "" }
                    val rolSolicitado = try { userSnap.child("rolSolicitado").value<String>() } catch (e: Exception) { null }
                    val solicitadoPor = try { userSnap.child("solicitadoPor").value<String>() } catch (e: Exception) { null }
                    val rolProgramado = try { userSnap.child("rolProgramado").value<String>() } catch (e: Exception) { null }
                    val fechaCambio = try { userSnap.child("fechaCambioProgramado").value<String>()?.toLongOrNull() } catch (e: Exception) { null }
                    listaUsuarios.add(
                        UsuarioApp(
                            uid = uid,
                            nombre = nombre ?: "Sin Nombre",
                            correo = correo ?: "Sin Correo",
                            rol = rol ?: "",
                            rolSolicitado = rolSolicitado,
                            solicitadoPor = solicitadoPor,
                            rolProgramado = rolProgramado,
                            fechaCambioProgramado = fechaCambio
                        )
                    )
                } catch (e: Exception) {
                }
            }
            listaUsuarios
        } catch (e: Exception) { emptyList() }
    }

    override suspend fun actualizarRolUsuario(uid: String, nuevoRol: String): Boolean {
        return try {
            network.FirebaseClient.database.reference("usuarios/$uid/rol").setValue(nuevoRol)
            true
        } catch (e: Exception) { false }
    }

    override suspend fun solicitarDegradoChofer(uidChofer: String, uidSolicitante: String): Boolean {
        val updates = mapOf("enEsperaDeDegrado" to true, "solicitadoPor" to uidSolicitante)
        return try { network.FirebaseClient.database.reference("usuarios/$uidChofer").updateChildren(updates); true } catch (e: Exception) { false }
    }

    override suspend fun aprobarDegrado(uidChofer: String): Boolean {
        val updates = mapOf("rol" to "", "enEsperaDeDegrado" to false, "solicitadoPor" to null)
        return try { network.FirebaseClient.database.reference("usuarios/$uidChofer").updateChildren(updates); true } catch (e: Exception) { false }
    }

    override suspend fun declinarDegrado(uidChofer: String): Boolean {
        val updates = mapOf("enEsperaDeDegrado" to false, "solicitadoPor" to null)
        return try { network.FirebaseClient.database.reference("usuarios/$uidChofer").updateChildren(updates); true } catch (e: Exception) { false }
    }

    override suspend fun programarCambioRol(uidUsuario: String, nuevoRol: String, tiempoEfectivo: Long): Boolean {
        val updates = mapOf("rolProgramado" to nuevoRol, "fechaCambioProgramado" to tiempoEfectivo)
        return try { network.FirebaseClient.database.reference("usuarios/$uidUsuario").updateChildren(updates); true } catch (e: Exception) { false }
    }

    override suspend fun ejecutarRolPendiente(uid: String, nuevoRol: String): Boolean {
        val updates = mapOf("rol" to nuevoRol, "rolProgramado" to null, "fechaCambioProgramado" to null)
        return try { network.FirebaseClient.database.reference("usuarios/$uid").updateChildren(updates); true } catch (e: Exception) { false }
    }

    override suspend fun solicitarCambioRol(uidUsuario: String, nuevoRol: String, uidSolicitante: String): Boolean {
        val updates = mapOf("rolSolicitado" to nuevoRol, "solicitadoPor" to uidSolicitante)
        return try { network.FirebaseClient.database.reference("usuarios/$uidUsuario").updateChildren(updates); true } catch (e: Exception) { false }
    }

    override suspend fun aprobarCambioRol(uidUsuario: String, rolAprobado: String): Boolean {
        val updates = mapOf("rol" to rolAprobado, "rolSolicitado" to null, "solicitadoPor" to null)
        return try { network.FirebaseClient.database.reference("usuarios/$uidUsuario").updateChildren(updates); true } catch (e: Exception) { false }
    }

    override suspend fun declinarCambioRol(uidUsuario: String): Boolean {
        val updates = mapOf("rolSolicitado" to null, "solicitadoPor" to null)
        return try { network.FirebaseClient.database.reference("usuarios/$uidUsuario").updateChildren(updates); true } catch (e: Exception) { false }
    }

    override suspend fun limpiarCambioPendiente(uid: String): Boolean {
        val updates = mapOf(
            "rolProgramado" to null,
            "fechaCambioProgramado" to null,
            "rolSolicitado" to null,
            "solicitadoPor" to null
        )
        return try {
            network.FirebaseClient.database.reference("usuarios/$uid").updateChildren(updates)
            true
        } catch (e: Exception) { false }
    }
    override suspend fun crearCuenta(email: String, password: String, nombre: String, rol: String): Boolean {
        return try {
            val idToken = FirebaseClient.auth.currentUser?.getIdToken(true) ?: return false
            val client = network.sharedHttpClient
            val body = kotlinx.serialization.json.buildJsonObject {
                put("data", kotlinx.serialization.json.buildJsonObject {
                    put("appVersion", kotlinx.serialization.json.JsonPrimitive(network.AppConfig.VERSION_CODE))
                    put("email", kotlinx.serialization.json.JsonPrimitive(email))
                    put("password", kotlinx.serialization.json.JsonPrimitive(password))
                    put("nombre", kotlinx.serialization.json.JsonPrimitive(nombre))
                    put("rol", kotlinx.serialization.json.JsonPrimitive(rol))
                })
            }
            val response = client.post(network.CloudFunctions.CREAR_CUENTA_USUARIO) {
                contentType(io.ktor.http.ContentType.Application.Json)
                header("Authorization", "Bearer $idToken")
                setBody(body.toString())
            }
            val responseText = response.body<String>()
            val exito = responseText.contains("\"success\":true") || responseText.contains("\"success\": true")
            if (!exito) println("[SmartVan] crearCuenta falló: ${responseText.take(200)}")
            exito
        } catch (e: Exception) {
            println("[SmartVan] crearCuenta excepción: ${e.message}")
            false
        }
    }

    override suspend fun eliminarCuenta(uid: String): Boolean {
        return try {
            val idToken = FirebaseClient.auth.currentUser?.getIdToken(true) ?: return false
            val client = network.sharedHttpClient
            val body = kotlinx.serialization.json.buildJsonObject {
                put("data", kotlinx.serialization.json.buildJsonObject {
                    put("appVersion", kotlinx.serialization.json.JsonPrimitive(network.AppConfig.VERSION_CODE))
                    put("uid", kotlinx.serialization.json.JsonPrimitive(uid))
                })
            }
            val response = client.post(network.CloudFunctions.ELIMINAR_CUENTA_USUARIO) {
                contentType(io.ktor.http.ContentType.Application.Json)
                header("Authorization", "Bearer $idToken")
                setBody(body.toString())
            }
            val responseText = response.body<String>()
            val exito = responseText.contains("\"success\":true") || responseText.contains("\"success\": true")
            if (!exito) println("[SmartVan] eliminarCuenta falló: ${responseText.take(200)}")
            exito
        } catch (e: Exception) {
            println("[SmartVan] eliminarCuenta excepción: ${e.message}")
            false
        }
    }

    override suspend fun resetearContrasena(uid: String, adminUid: String): String? {
        return try {
            val client = network.sharedHttpClient
            val idToken = FirebaseClient.auth.currentUser?.getIdToken(true) ?: ""
            val requestBody = kotlinx.serialization.json.buildJsonObject {
                put("data", kotlinx.serialization.json.buildJsonObject {
                    put("appVersion", kotlinx.serialization.json.JsonPrimitive(network.AppConfig.VERSION_CODE))
                    put("uid", kotlinx.serialization.json.JsonPrimitive(uid))
                })
            }
            val response = client.post(network.CloudFunctions.RESETEAR_CONTRASENA) {
                contentType(io.ktor.http.ContentType.Application.Json)
                header("Authorization", "Bearer $idToken")
                setBody(requestBody.toString())
            }
            val body = response.body<ResetResponse>()
            if (body.result.success) {
                body.result.tempPassword
            } else {
                null
            }
        } catch (e: Exception) {
            null
        }
    }

    override suspend fun requiereCambioContrasena(uid: String): Boolean {
        return try {
            val snapshot = network.FirebaseClient.database.reference("usuarios/$uid/requiereCambio").valueEvents.first()
            snapshot.exists && snapshot.value<Boolean>()
        } catch (e: Exception) {
            false
        }
    }

    override suspend fun cambiarContrasena(nuevaContrasena: String): Boolean {
        return try {
            val user = FirebaseClient.auth.currentUser ?: return false
            user.updatePassword(nuevaContrasena)
            true
        } catch (e: Exception) {
            false
        }
    }

    override suspend fun limpiarFlagCambio(uid: String): Boolean {
        return try {
            network.FirebaseClient.database.reference("usuarios/$uid/requiereCambio").removeValue()
            true
        } catch (e: Exception) {
            false
        }
    }

}